import React, { useId, useMemo } from 'react';
import { BEAN_LOGO_URL } from '../../constants/brand';
import { qrLayout } from '../../services/qr';

interface QrCodeProps {
    data: string;
    /** Modulernas färg. Bakgrunden är alltid vit, även i mörkt läge. */
    color?: string;
    className?: string;
    /** Bönan i ett runt hål mitt i koden. */
    showIcon?: boolean;
    /** Annan bild än bönan i hålet. */
    iconUrl?: string;
    /** Vit kant runt koden, i moduler. Minst 2 behövs för säker skanning. */
    quietZone?: number;
    /** Text för skärmläsare. */
    label?: string;
}

/**
 * Den enda QR-koden i appen: säljkort, helskärm för kunden, reklamblad,
 * etiketter och faktura använder alla den här. Koden ritas som en SVG med
 * vit bakgrund och vit kant, och bönan sitter i ett runt hål som följer
 * rutnätet så att kanten runt den blir jämn.
 */
export const QrCode: React.FC<QrCodeProps> = ({
    data,
    color = '#1C1510',
    className = '',
    showIcon = false,
    iconUrl = BEAN_LOGO_URL,
    quietZone = 2,
    label = 'QR-kod',
}) => {
    const clipId = useId().replace(/:/g, '');
    const { size, path, hole } = useMemo(
        () => qrLayout(data, { withHole: showIcon, quiet: quietZone }),
        [data, showIcon, quietZone]
    );
    const fill = color.startsWith('#') ? color : `#${color}`;

    // Hela bilden visas (som tidigare, object-contain) på en vit rund platta
    // och tar ~80 % av hålet, så bönan aldrig beskärs och har jämn luft runt sig.
    const bean = hole ? hole.r * 2 * 0.8 : 0;

    return (
        <svg
            viewBox={`0 0 ${size} ${size}`}
            className={`block ${className || 'w-full h-auto'}`}
            role="img"
            aria-label={label}
            shapeRendering="crispEdges"
        >
            <rect width={size} height={size} fill="#FFFFFF" />
            <path d={path} fill={fill} />
            {hole && (
                <>
                    <defs>
                        <clipPath id={clipId}>
                            <circle cx={hole.cx} cy={hole.cy} r={hole.r} />
                        </clipPath>
                    </defs>
                    <circle cx={hole.cx} cy={hole.cy} r={hole.r} fill="#FFFFFF" />
                    <image
                        href={iconUrl}
                        x={hole.cx - bean / 2}
                        y={hole.cy - bean / 2}
                        width={bean}
                        height={bean}
                        preserveAspectRatio="xMidYMid meet"
                        clipPath={`url(#${clipId})`}
                        shapeRendering="auto"
                    />
                </>
            )}
        </svg>
    );
};
